import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {BlockedIcon} from '@primer/octicons-react'
import {Link, Token} from '@primer/react'

import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from './HeaderBlockedBySummary.module.css'
import dividerLineStyles from './HeaderMetadataDivider.module.css'

export type HeaderBlockedBySummaryFragment = RelayCompat<
  Pick<IssueSecondaryNode, 'state' | 'issueDependenciesSummary' | 'blockedBy'>
>

export type HeaderBlockedBySummaryProps = {
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  size?: 'xlarge' | 'small'
}

export const HeaderBlockedBySummary = ({
  blockedBySecondaryData: node,
  size = 'xlarge',
}: HeaderBlockedBySummaryProps) => {
  const {blockedBy: blockedByCount} = node?.issueDependenciesSummary ?? {}
  if (!node || !blockedByCount) return <></>
  if (node.state === 'CLOSED') return <></>

  const blockedByIssue = blockedByCount === 1 ? node?.blockedBy?.nodes?.[0] || undefined : undefined
  return (
    <>
      {size !== 'small' && <span className={dividerLineStyles.dividerLine} />}
      <HeaderBlockedBySummaryInternal blockedByCount={blockedByCount} size={size} blockedByIssue={blockedByIssue} />
    </>
  )
}

export type HeaderBlockedBySummaryInternalProps = {
  blockedByCount: number
  size: 'xlarge' | 'small'
  blockedByIssue?: {
    titleHTML: string
    number: number
    url: string
    repository: {
      name: string
      owner: {
        login: string
      }
    }
  }
}

function BlockedByIssueLink({
  blockedByIssue,
  size,
}: {
  blockedByIssue: NonNullable<HeaderBlockedBySummaryInternalProps['blockedByIssue']>
  size: 'xlarge' | 'small'
}) {
  const {titleHTML, number, url, repository} = blockedByIssue
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })
  return (
    <Link
      className={styles.issueLink}
      aria-label={`Blocked by issue ${titleHTML}`}
      inline
      muted
      href={url}
      {...hovercardAttrs}
    >
      {size === 'small' ? (
        `#${number}`
      ) : (
        // Server-sanitized HTML from GraphQL titleHTML field
        // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
        <VerifiedHTMLText html={titleHTML as SafeHTMLString} className="markdown-title" />
      )}
    </Link>
  )
}

export function HeaderBlockedBySummaryInternal({
  blockedByCount,
  size,
  blockedByIssue,
}: HeaderBlockedBySummaryInternalProps) {
  const label = `Blocked by ${blockedByCount} issue${blockedByCount === 1 ? '' : 's'}`

  const blockedByIssueLink = blockedByIssue ? (
    <BlockedByIssueLink blockedByIssue={blockedByIssue} size={size} />
  ) : undefined

  if (size === 'small') {
    return (
      <div className={styles.smallSummary}>
        <BlockedIcon className="fgColor-danger" size={14} />
        <span className={styles.visualLabel}>{blockedByIssueLink || blockedByCount}</span>
        <span className="sr-only">{label}</span>
      </div>
    )
  }

  const leadingText = `Blocked by${blockedByIssueLink ? ':' : ''}`
  return (
    <Token
      text={
        <>
          <span className={styles.visualLabel}>
            {leadingText} {blockedByIssueLink || blockedByCount}
          </span>
          <span className="sr-only">{label}</span>
        </>
      }
      leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
      className={styles.token}
      size="xlarge"
    />
  )
}
